import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { WorkflowInstanceId } from "../../../shared/schema/schema";
import { createDisclosureTriggerComponent } from "../../../shared/utils/create-disclosure-trigger-component";
import { useApi } from "../../../shared/utils/use-api";
import { WORKFLOW_DASHBOARD_INSTANCES_QUERY } from "../workflow.graphql";

interface Props {
  id: WorkflowInstanceId;
  isOpen: boolean;
  onClose: () => void;
}

export default function CancelWorkflowInstanceAlertDialog(props: Props) {
  const leastDestructiveRef = React.useRef<HTMLButtonElement | null>(null);

  return (
    <AlertDialog
      isOpen={props.isOpen}
      leastDestructiveRef={leastDestructiveRef}
      onClose={props.onClose}
    >
      <AlertDialogOverlay>
        <AlertContent leastDestructiveRef={leastDestructiveRef} {...props} />
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

function AlertContent(props: Props & { leastDestructiveRef: React.RefObject<HTMLButtonElement> }) {
  const { api } = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      api.post("./workflow_instances/:workflowInstanceId/cancel", {
        path: { workflowInstanceId: props.id },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([WORKFLOW_DASHBOARD_INSTANCES_QUERY]);
      props.onClose();
      toast({
        title: `Instance cancelled`,
        status: "success",
        position: "top-right",
      });
    },
    onError: (error) => {
      toast({
        title: `Error in cancelling instance`,
        description: String(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  return (
    <AlertDialogContent>
      <AlertDialogHeader fontSize="lg" fontWeight="bold">
        Stop Instance
      </AlertDialogHeader>

      <AlertDialogBody>Are you sure you want to stop this workflow instance?</AlertDialogBody>

      <AlertDialogFooter>
        <Button ref={props.leastDestructiveRef} onClick={props.onClose}>
          Nevermind
        </Button>
        <Button
          colorScheme="red"
          isLoading={mutation.isLoading}
          ml={3}
          onClick={() => mutation.mutate()}
        >
          Stop
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  );
}

export const CancelWorkflowInstanceAlertDialogTrigger = createDisclosureTriggerComponent<{
  id: WorkflowInstanceId;
}>({
  displayName: "CancelWorkflowInstanceAlertDialogTrigger",
  render: (props) => (
    <CancelWorkflowInstanceAlertDialog
      id={props.id}
      isOpen={props.disclosure.isOpen}
      onClose={props.disclosure.onClose}
    />
  ),
});
